import React from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Popover,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import styles from "./index.module.css";
import { DISPLAY_STATUS, normalizeDisplayStatus } from "../../../utils/pricingOptions";

const formatPresentationMoney = (value, currencyCode = "CAD") => {
  const amount = Number(value || 0);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const code = String(currencyCode || "CAD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
    }).format(safeAmount);
  } catch (_) {
    return `${code} ${safeAmount.toFixed(2)}`;
  }
};

const PricingSettingsPopover = ({
  anchorEl,
  open,
  onClose,
  pricingGridMode = false,
  onPricingGridModeChange,
  rows = [],
  pricingCurrencyCode = "CAD",
}) => {
  const previewRows = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (String(row?.priceType || "").trim().toLowerCase() !== "breakdown") return false;
    return normalizeDisplayStatus(row?.pricingOption) === DISPLAY_STATUS.DISPLAY_TO_CLIENT;
  });

  const showEmptyState = pricingGridMode && previewRows.length < 1;
  const showPreviewTable = pricingGridMode && previewRows.length > 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Box className={styles.settingsPopover}>
        <Typography variant="subtitle1" className={styles.settingsPopoverTitle}>
          Pricing Settings
        </Typography>
        <Divider className={styles.settingsDivider} />
        <Typography variant="overline" className={styles.settingsSectionLabel}>
          Presentation
        </Typography>
        <FormControlLabel
          className={styles.settingsToggleRow}
          control={(
            <Switch
              checked={pricingGridMode}
              onChange={(event) => onPricingGridModeChange?.(event.target.checked)}
              inputProps={{ "aria-label": "Toggle pricing grid mode" }}
            />
          )}
          label="Grid Mode"
        />
        <Typography variant="body2" className={styles.settingsHelperText}>
          Show customer-facing pricing rows as a quantity/price grid on presentations.
        </Typography>

        {showPreviewTable ? (
          <Box className={styles.previewSection}>
            <Typography variant="subtitle2" className={styles.previewTitle}>
              Pricing grid preview
            </Typography>
            <Table size="small" className={styles.previewTable} aria-label="Pricing grid preview">
              <TableHead>
                <TableRow>
                  <TableCell className={styles.previewHeaderCell}>Quantity</TableCell>
                  <TableCell className={styles.previewHeaderCell}>Client Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, index) => (
                  <TableRow key={String(row?.id || row?.tempRowId || index)}>
                    <TableCell className={styles.previewBodyCell}>{row?.quantity ?? ""}</TableCell>
                    <TableCell className={styles.previewBodyCell}>
                      {formatPresentationMoney(row?.clientPrice, pricingCurrencyCode)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : null}

        {showEmptyState ? (
          <Typography variant="body2" className={styles.previewEmptyState}>
            Add customer-facing pricing breakdowns in the Pricing table to preview Grid Mode.
          </Typography>
        ) : null}

        {pricingGridMode ? (
          <Typography variant="caption" className={styles.previewFooterNote}>
            Presentation only - does not affect cart or Sales Order pricing.
          </Typography>
        ) : null}
      </Box>
    </Popover>
  );
};

export default PricingSettingsPopover;
